import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { downloadAnalysisPdf } from "@/lib/pdf";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import QuestionnaireDeclarations from "@/components/QuestionnaireDeclarations";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ChevronLeft, Download, Calendar, CheckCircle2, Home, Scissors,
  AlertCircle, Eye, Loader2, Receipt, ShoppingBag, Lock, Crown,
  MessageCircle, RefreshCw, GitCompare,
  Send, Link as LinkIcon, Mail, Check, FileText, Share2, Info, ChevronDown, ChevronUp,
  Droplet, BarChart3, TrendingUp, TrendingDown, Minus,
  Sun, Aperture, Flashlight, ChevronsLeftRight, Images,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useTricoVocab } from "@/lib/tricoVocab";
import { ANALYSIS_SLOTS } from "@/lib/analysisSlots";

// Thumbnail prodotto in stile e-commerce (allineato a ProductEcommerceCard su
// mobile): mostra img_prodotto se lo ScoreEngine l'ha fornita, altrimenti
// un'icona placeholder — mai un riquadro vuoto.
function ProductThumb({ src, alt }) {
  const [errored, setErrored] = useState(false);
  return (
    <div className="w-20 h-20 shrink-0 rounded-lg bg-white border border-[#E5E5E0] flex items-center justify-center overflow-hidden">
      {src && !errored ? (
        <img
          src={src}
          alt={alt || ""}
          className="w-full h-full object-contain"
          onError={() => setErrored(true)}
        />
      ) : (
        <ShoppingBag className="w-7 h-7 text-[#C5A059]" strokeWidth={1.5} />
      )}
    </div>
  );
}

const formatEuro = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "";
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(Number(value));
};

// ---- Scalp & Hair Profile — indici sintetici TricoProtocol -----------------
// I bucket/etichette arrivano già come category_code dal backend (calcolati
// da Trico_get_categorization.py) — qui si traduce solo il codice via
// useTricoVocab (frontend/src/i18n/{it,en}/categories.json), niente più
// soglie duplicate lato client (vedi piano "dobbiamo-rivedere-il-processo").

// Severità 0..3 (solo per il colore del badge) per category_code — la
// polarità buono/cattivo di ciascun campo vive nei NOMI delle fasce (vedi
// Trico_get_categorization.py), non nell'ordine numerico, quindi qui si
// mappa il singolo codice invece di derivare la severità da category_order.
const SEVERITY_BY_CODE = {
  risk_index: { excellent: 0, good: 1, fair: 2, poor: 3, critical: 4 },
  wellness: { good: 0, mild_imbalance: 1, moderate_imbalance: 2, marked_imbalance: 3 },
  global_health: { excellent: 0, good: 1, fair: 2, poor: 3, critical: 4 },
  occlusion: { absent: 0, mild: 1, moderate: 2, severe: 3 },
  porphyrin: { absent: 0, present: 2, severe: 3 },
  sebum: { normal: 0, oily: 1, veryOily: 2 },
  vlm: { absent: 0, mild: 1, moderate: 2, severe: 3 },
  // Diametro del fusto: polarità invertita rispetto a category_order (robusto
  // = fascia più alta ma è lo stato più sano) — vedi Trico_get_categorization.py.
  diameter: { very_thin: 3, thinned: 2, normal: 1, robust: 0 },
  density: { reduced: 3, mildly_reduced: 2, normal: 0 },
  diversity: { absent_minimal: 0, mild_moderate: 2, marked: 3 },
};
const codeSeverity = (field, code) => SEVERITY_BY_CODE[field]?.[code] ?? null;

// 5 livelli (0..4): "poor" e "critical" di risk_index/global_health restano
// entrambi nella fascia rossa ma con toni diversi, per non appiattire le due
// fasce di rischio più gravi sullo stesso colore.
const SEVERITY_PILLS = [
  " text-emerald-700 ",
  " text-amber-700 ",
  " text-orange-700",
  " text-red-700 ",
  " text-red-900 ",
];
const SEVERITY_RING = ["#41A946", "#F0A93A", "#F57C00", "#C62828", "#8E1B1B"];



// I due gauge di ScalpHealthProfile condividono il livello di severità 0
// (verde) tra "excellent" (global_health) e "good" (wellness), ma sono fasce
// cliniche diverse: "excellent" è il picco assoluto della scala a 5 livelli,
// "good" è il picco della scala a 4 livelli del benessere cute. Qui si
// differenzia solo la tonalità dell'anello, non il livello di severità.
const GAUGE_RING_OVERRIDE = {
  global_health: { excellent: "#2E7D32" },
  wellness: { good: "#7CB342" },
};

// Gauge circolare riusabile per un singolo indice sintetico: anello colorato
// in base alla severità della fascia (category_code) + fascia stessa dentro
// il cerchio + nome dell'indice come label descrittiva sotto il gauge.
function IndexGauge({ testId, indexKey, entry, namespace, vocab }) {
  const R = 52;
  const CIRC = 2 * Math.PI * R;
  const severity = entry ? codeSeverity(namespace, entry.category_code) : null;
  const qualLabel = entry ? vocab.categoryLabel(namespace, entry.category_code) : null;
  const ringOverride = entry ? GAUGE_RING_OVERRIDE[namespace]?.[entry.category_code] : null;
  const ringColor = ringOverride || (severity != null ? SEVERITY_RING[severity] : "#C5A059");
  // Il gauge copre l'intera scala (tutte le fasce), non solo la fascia
  // corrente: posizione continua = (fasce già superate + posizione dentro
  // la fascia attuale) / fasce totali. Usare solo category_order/total
  // saturerebbe sempre il gauge per chi è nell'ultima fascia, ignorando
  // dove si trova esattamente dentro quella fascia (position_in_category).
  const ringPct = entry
    ? Math.max(0, Math.min(100, ((entry.category_order - 1 + (entry.position_in_category ?? 0)) / entry.total_categories) * 100))
    : 0;

  return (
    <div className="flex flex-col items-center justify-center" data-testid={testId}>
      <div className="relative w-32 h-32">
        <svg viewBox="0 0 120 120" className="w-32 h-32 -rotate-90">
          <circle cx="60" cy="60" r={R} fill="none" stroke="#EFEBE3" strokeWidth="10" />
          <circle
            cx="60" cy="60" r={R} fill="none"
            stroke={ringColor} strokeWidth="10" strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={CIRC * (1 - ringPct / 100)}
            style={{ transition: "stroke-dashoffset 0.6s ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2">
          <p className="font-serif-display text-3xl text-[#1A1A1A] leading-none">
            {entry ? Math.round(ringPct) : "—"}
          </p>
          <span className="text-[10px] font-medium mt-1.5" style={{ color: ringColor }}>
            {qualLabel || "—"}
          </span>
        </div>
      </div>
      <p className="text-[10px] uppercase tracking-wider text-[#737373] font-medium mt-3 text-center">
        {vocab.indexName(indexKey)}
      </p>
    </div>
  );
}

function ScalpHealthProfile({ analysis }) {
  const vocab = useTricoVocab();
  const indici = analysis?.indici || [];
  if (indici.length === 0) return null;

  const byKey = Object.fromEntries(indici.map((i) => [i.key, i]));
  const global = byKey.hair_global_health_index;
  const wellness = byKey.scalp_wellness_index;
  const loss = byKey.hair_loss_index;
  const fall = byKey.hair_fall_index;
  const aging = byKey.hair_aging_index;

  return (
    <div className="col-span-12 lg:col-span-6 rounded-2xl border border-[#E5E5E0] bg-white p-6 sm:p-8 animate-fade-up" data-testid="scalp-health-profile">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#FAF6EE] border border-[#C5A059]/30 flex items-center justify-center shrink-0">
          <Droplet className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium">Indici sintetici TricoProtocol</p>
          <p className="font-serif-display text-2xl text-[#1A1A1A] leading-tight">Profilo cute &amp; capelli</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-12">
        <IndexGauge
          testId="scalp-global-health-index"
          indexKey="hair_global_health_index"
          entry={global}
          namespace="global_health"
          vocab={vocab}
        />
        <IndexGauge
          testId="scalp-wellness-index"
          indexKey="scalp_wellness_index"
          entry={wellness}
          namespace="wellness"
          vocab={vocab}
        />
      </div>

      <div className="mt-6 pt-2 divide-y divide-[#F0EDE7] border-t border-[#F0EDE7]">
        <RiskIndexRow testId="scalp-hair-loss-index" indexKey="hair_loss_index" entry={loss} vocab={vocab} />
        <RiskIndexRow testId="scalp-hair-fall-index" indexKey="hair_fall_index" entry={fall} vocab={vocab} />
        <RiskIndexRow testId="scalp-hair-aging-index" indexKey="hair_aging_index" entry={aging} vocab={vocab} />
      </div>
    </div>
  );
}

// Riga con track + marker per un singolo indice di rischio (risk_index):
// stessa track/marker style della card "Confronto oggettivo" del follow-up
// (vedi FollowupIndexTrack) invece della barra piena precedente — qui c'è un
// solo marker (valore corrente, non c'è baseline da confrontare). Posizione
// sulla scala: stessa formula continua del gauge (fasce già superate +
// posizione dentro la fascia attuale, su fasce totali).
function RiskIndexRow({ testId, indexKey, entry, vocab }) {
  const qualLabel = entry ? vocab.categoryLabel("risk_index", entry.category_code) : null;
  const severity = entry ? codeSeverity("risk_index", entry.category_code) : null;
  const pillCls = severity != null ? SEVERITY_PILLS[severity] : "bg-[#FAFAFA] text-[#A3A3A3] border-[#E5E5E0]";
  const markerColor = severity != null ? SEVERITY_RING[severity] : "#D4D4D4";
  const posPct = entry
    ? Math.max(0, Math.min(100, ((entry.category_order - 1 + (entry.position_in_category ?? 0)) / entry.total_categories) * 100))
    : 0;

  return (
    <div className="py-2.5" data-testid={testId}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="text-sm text-[#1A1A1A]">{vocab.indexName(indexKey)}</span>
        <span className={`text-[10px] font-medium px-2.5 py-0.5 rounded-full border whitespace-nowrap shrink-0 ${pillCls}`}>
          {qualLabel || "N/A"}
        </span>
      </div>
      <div className="relative h-2 rounded-full bg-[#EFEBE3]">
        <div
          className="absolute w-3.5 h-3.5 rounded-full border-2 border-white top-1/2 shadow-sm"
          style={{ left: `${posPct}%`, background: markerColor, transform: "translate(-50%, -50%)", transition: "left 0.6s ease" }}
        />
      </div>
    </div>
  );
}

// ---- Follow-up: evoluzione indici rispetto alla baseline -----------------
// Sostituisce il testo piatto "hair_loss_index: stabile (excellent -> excellent)"
// (trico_pipeline.compare_followup_scores -> analysis.confronto) con una card
// grafica: una track per indice con marker baseline (grigio) e attuale
// (colorato per fascia) sulla stessa scala 0..total_categories-1 usata dai
// gauge di ScalpHealthProfile. Namespace/severità sono derivati qui dal
// category_code (stesso pattern di RiskIndexRow/IndexGauge), il backend
// passa solo i codici e le posizioni di fascia.
const NAMESPACE_BY_INDEX_KEY = {
  hair_loss_index: "risk_index",
  hair_fall_index: "risk_index",
  hair_aging_index: "risk_index",
  scalp_wellness_index: "wellness",
  hair_global_health_index: "global_health",
};

const FOLLOWUP_DIRECTION_STYLE = {
  improved: { color: "#2E7D32", Icon: TrendingUp, label: "Migliorato" },
  worsened: { color: "#C62828", Icon: TrendingDown, label: "Peggiorato" },
  stable: { color: "#8A6B2E", Icon: Minus, label: "Stabile" },
};

// Per risk_index/wellness/global_health (le uniche namespace usate dai 5
// indici di follow-up) la severità in SEVERITY_BY_CODE è già ordinata come
// il category_order del backend (0 = fascia migliore, N-1 = fascia
// peggiore) — vedi commento su SEVERITY_BY_CODE più sopra. Si posiziona il
// marker sulla severità invece che su category_order/total_categories così
// la track funziona anche quando questi ultimi non sono disponibili (analisi
// di follow-up salvate prima dell'introduzione di confronto_indici, vedi
// parseConfrontoText più sotto).
const namespaceMaxSeverity = (namespace) => {
  const values = Object.values(SEVERITY_BY_CODE[namespace] || {});
  return values.length ? Math.max(...values) : 0;
};

function FollowupIndexTrack({ item, vocab }) {
  const namespace = NAMESPACE_BY_INDEX_KEY[item.key];
  const baseSeverity = codeSeverity(namespace, item.base_category_code);
  const newSeverity = codeSeverity(namespace, item.new_category_code);
  const maxSeverity = namespaceMaxSeverity(namespace);
  const posPct = (severity) => (severity != null && maxSeverity > 0 ? (severity / maxSeverity) * 100 : 50);
  const basePct = posPct(baseSeverity);
  const newPct = posPct(newSeverity);

  let direction = "stable";
  if (baseSeverity != null && newSeverity != null) {
    if (newSeverity < baseSeverity) direction = "improved";
    else if (newSeverity > baseSeverity) direction = "worsened";
  }
  const { color, Icon, label } = FOLLOWUP_DIRECTION_STYLE[direction];
  const newColor = newSeverity != null ? SEVERITY_RING[newSeverity] : "#C5A059";

  return (
    <div className="py-3.5" data-testid={`followup-index-track-${item.key}`}>
      <div className="flex items-center justify-between gap-3 mb-2.5">
        <span className="text-sm text-[#1A1A1A] font-medium">{vocab.indexName(item.key)}</span>
        <span className="inline-flex items-center gap-1 text-[11px] font-medium shrink-0" style={{ color }}>
          <Icon className="w-3.5 h-3.5" /> {label}
        </span>
      </div>
      <div className="relative h-2 rounded-full bg-[#EFEBE3]">
        <div
          className="absolute top-0 h-full rounded-full opacity-40"
          style={{
            left: `${Math.min(basePct, newPct)}%`,
            width: `${Math.abs(newPct - basePct)}%`,
            background: color,
            transition: "left 0.6s ease, width 0.6s ease",
          }}
        />
        <div
          className="absolute w-3 h-3 rounded-full border-2 border-white bg-[#A3A3A3] top-1/2 shadow-sm"
          style={{ left: `${basePct}%`, transform: "translate(-50%, -50%)", transition: "left 0.6s ease" }}
          title={`Baseline: ${vocab.categoryLabel(namespace, item.base_category_code)}`}
        />
        <div
          className="absolute w-3.5 h-3.5 rounded-full border-2 border-white top-1/2 shadow-sm"
          style={{ left: `${newPct}%`, background: newColor, transform: "translate(-50%, -50%)", transition: "left 0.6s ease" }}
          title={`Attuale: ${vocab.categoryLabel(namespace, item.new_category_code)}`}
        />
      </div>
      <div className="flex justify-between mt-1.5 text-[11px] text-[#737373]">
        <span>Baseline: {vocab.categoryLabel(namespace, item.base_category_code) || "N/A"}</span>
        <span className="font-medium text-[#1A1A1A]">Ora: {vocab.categoryLabel(namespace, item.new_category_code) || "N/A"}</span>
      </div>
    </div>
  );
}

// Fallback per analisi di follow-up salvate prima dell'introduzione del
// campo strutturato confronto_indici: il testo di analysis.confronto ha
// sempre il formato fisso "key: trend (base_code -> new_code)" generato da
// trico_pipeline.compare_followup_scores, quindi si può ricostruire la
// stessa struttura senza richiedere un ricalcolo lato backend.
const CONFRONTO_LINE_RE = /^(\w+):\s*(stabile|spostato)\s*\(([^-]+?)\s*->\s*([^)]+?)\)$/;
function parseConfrontoText(text) {
  if (!text) return [];
  return text
    .split(";")
    .map((line) => line.trim())
    .map((line) => {
      const m = line.match(CONFRONTO_LINE_RE);
      if (!m) return null;
      const [, key, trend, baseCode, newCode] = m;
      if (!NAMESPACE_BY_INDEX_KEY[key]) return null;
      return { key, trend, base_category_code: baseCode.trim(), new_category_code: newCode.trim() };
    })
    .filter(Boolean);
}

function FollowupIndicesChart({ analysis }) {
  const vocab = useTricoVocab();
  const items = analysis?.confronto_indici?.length
    ? analysis.confronto_indici
    : parseConfrontoText(analysis?.confronto);

  if (items.length === 0) {
    if (!analysis?.confronto) return null;
    return (
      <div className="rounded-xl bg-white border border-[#E5E5E0] p-5" data-testid="followup-confronto">
        <p className="text-[11px] uppercase tracking-[0.15em] text-[#737373] font-medium mb-2">
          Confronto oggettivo
        </p>
        <p className="text-[15px] text-[#1A1A1A] leading-relaxed">{analysis.confronto}</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-white border border-[#E5E5E0] p-5" data-testid="followup-confronto">
      <p className="text-[11px] uppercase tracking-[0.15em] text-[#737373] font-medium mb-1">
        Confronto oggettivo
      </p>
      <p className="text-xs text-[#737373] mb-1">Evoluzione degli indici sintetici rispetto alla baseline</p>
      <div className="divide-y divide-[#F0EDE7]">
        {items.map((item) => (
          <FollowupIndexTrack key={item.key} item={item} vocab={vocab} />
        ))}
      </div>
    </div>
  );
}

// ---- Follow-up: confronto fotografico "Prima/Dopo" -------------------------
// Card affiancata a FollowupIndicesChart (stessa riga): selezione della luce
// (stessi 3 slot fissi di ANALYSIS_SLOTS — vedi frontend/src/lib/analysisSlots.js)
// + slider trascinabile che sovrappone la foto della baseline (previous
// analysis, caricata a parte da AnalysisDetail) e quella dell'analisi
// corrente per la stessa luce/zona.
const LIGHT_ICON_BY_SLOT = {
  "vertex-bianca": Sun,
  "vertex-polarizzata": Aperture,
  "vertex-uv": Flashlight,
};

function PhotoCompareSlider({ beforeSrc, afterSrc, beforeLabel, afterLabel }) {
  const [pos, setPos] = useState(50);
  return (
    <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-[#FAFAFA] select-none">
      <img src={afterSrc} alt={afterLabel} draggable={false} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={beforeSrc} alt={beforeLabel} draggable={false} className="absolute inset-0 w-full h-full object-cover" />
      </div>
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white/90 shadow-md pointer-events-none"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
      />
      <div
        className="absolute top-1/2 w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center pointer-events-none"
        style={{ left: `${pos}%`, transform: "translate(-50%, -50%)" }}
      >
        <ChevronsLeftRight className="w-4 h-4 text-[#1A1A1A]" />
      </div>
      <input
        type="range"
        min="0"
        max="100"
        value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
        aria-label="Trascina per confrontare prima e dopo"
        data-testid="followup-photo-slider-input"
      />
      <span className="absolute top-2 left-2 text-[10px] font-medium px-2 py-0.5 rounded-full bg-black/60 text-white pointer-events-none">
        {beforeLabel}
      </span>
      <span className="absolute top-2 right-2 text-[10px] font-medium px-2 py-0.5 rounded-full bg-black/60 text-white pointer-events-none">
        {afterLabel}
      </span>
    </div>
  );
}

function PhotoCompareCard({ analysis, baselinePhotos, baselineCreatedAt }) {
  const currentPhotos = analysis?.photos || [];
  const availableSlots = ANALYSIS_SLOTS.filter(
    (slot) =>
      currentPhotos.some((p) => p.label === slot.label) &&
      (baselinePhotos || []).some((p) => p.label === slot.label)
  );
  const availableIds = availableSlots.map((s) => s.id).join(",");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (availableSlots.length > 0 && !availableSlots.some((s) => s.id === selectedId)) {
      setSelectedId(availableSlots[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableIds]);

  // baselinePhotos === null → ancora in caricamento (best-effort, non blocca
  // il resto della pagina). [] → caricate ma nessuna foto in comune: card
  // nascosta invece di mostrare uno stato vuoto poco utile.
  if (baselinePhotos === null) {
    return (
      <div
        className="rounded-xl bg-white border border-[#E5E5E0] p-5 flex items-center justify-center min-h-[220px]"
        data-testid="followup-photo-compare-loading"
      >
        <Loader2 className="w-5 h-5 text-[#C5A059] animate-spin" />
      </div>
    );
  }
  if (availableSlots.length === 0) return null;

  const selectedSlot = availableSlots.find((s) => s.id === selectedId) || availableSlots[0];
  const currentPhoto = currentPhotos.find((p) => p.label === selectedSlot.label);
  const basePhoto = baselinePhotos.find((p) => p.label === selectedSlot.label);
  const currentDateLabel = analysis?.created_at
    ? new Date(analysis.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })
    : "Ora";
  const baselineDateLabel = baselineCreatedAt
    ? new Date(baselineCreatedAt).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })
    : "Prima";

  return (
    <div className="rounded-xl bg-white border border-[#E5E5E0] p-5" data-testid="followup-photo-compare">
      <div className="flex items-center gap-2 mb-1">
        <Images className="w-4 h-4 text-[#8A6B2E]" />
        <p className="text-[11px] uppercase tracking-[0.15em] text-[#737373] font-medium">Confronto fotografico</p>
      </div>
      <p className="text-xs text-[#737373] mb-3">Trascina per confrontare la foto attuale con la baseline</p>

      <div className="flex flex-wrap gap-2 mb-4">
        {availableSlots.map((slot) => {
          const Icon = LIGHT_ICON_BY_SLOT[slot.id] || Sun;
          const active = slot.id === selectedSlot.id;
          return (
            <button
              key={slot.id}
              type="button"
              onClick={() => setSelectedId(slot.id)}
              data-testid={`followup-photo-light-${slot.id}`}
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                active
                  ? "bg-[#1A1A1A] border-[#1A1A1A] text-white"
                  : "bg-white border-[#E5E5E0] text-[#1A1A1A] hover:border-[#C5A059]"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {slot.label.replace("Vertex — ", "")}
            </button>
          );
        })}
      </div>

      {currentPhoto && basePhoto ? (
        <PhotoCompareSlider
          beforeSrc={`data:image/jpeg;base64,${basePhoto.data}`}
          afterSrc={`data:image/jpeg;base64,${currentPhoto.data}`}
          beforeLabel={`Prima · ${baselineDateLabel}`}
          afterLabel={`Dopo · ${currentDateLabel}`}
        />
      ) : (
        <p className="text-sm text-[#A3A3A3]">Foto non disponibile per questa luce.</p>
      )}
    </div>
  );
}

// ---- Profilo tecnico — parametri grezzi di analysis.trico_engine_raw.scores
// in formato tabellare compatto (Parametro / Tag), senza progress bar.
// Mostrato accanto a ScalpHealthProfile (stessa riga della bento grid).
function TechRow({ testId, label, namespace, code }) {
  const vocab = useTricoVocab();
  const severity = code ? codeSeverity(namespace, code) : null;
  const pillCls = severity != null ? SEVERITY_PILLS[severity] : "bg-[#FAFAFA] text-[#A3A3A3] border-[#E5E5E0]";
  const qualLabel = code ? vocab.categoryLabel(namespace, code) : null;

  return (
    <div className="grid grid-cols-12 gap-3 items-center py-2.5 border-b border-[#F0EDE7] last:border-b-0" data-testid={testId}>
      <span className="col-span-9 text-sm text-[#1A1A1A]">{label}</span>
      <span className="col-span-3 flex justify-end">
        <span className={`text-[10px] font-medium px-2.5 py-0.5 rounded-full border whitespace-nowrap ${pillCls}`}>
          {qualLabel || "N/A"}
        </span>
      </span>
    </div>
  );
}

function TechnicalProfileCard({ analysis }) {
  const scores = analysis?.trico_engine_raw?.scores;
  if (!scores || Object.keys(scores).length === 0) return null;

  const diam = scores.hair_fiber_diameter_um;
  const diamCat = scores.hair_fiber_diameter_category;
  const mini = scores.miniaturization;
  const density = scores.density;
  const porphyrin = scores.porphyrin;
  const sebum = scores.sebum_level;
  const vlm = scores.vlm?.result;

  return (
    <div className="col-span-12 lg:col-span-6 rounded-2xl border border-[#E5E5E0] bg-white p-6 sm:p-8 animate-fade-up" data-testid="technical-profile-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#FAFAFA] border border-[#E5E5E0] flex items-center justify-center shrink-0">
          <BarChart3 className="w-5 h-5 text-[#737373]" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium">Indici singoli TricoProtocol</p>
          <p className="font-serif-display text-2xl text-[#1A1A1A] leading-tight">Profilo tecnico</p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-3 pb-2 border-b border-[#E5E5E0] mb-1">
        <span className="col-span-9 text-[10px] uppercase tracking-wider text-[#A3A3A3] font-medium">Parametro</span>
        <span className="col-span-3 text-[10px] uppercase tracking-wider text-[#A3A3A3] font-medium text-right">Valutazione</span>
      </div>

      {diam != null && (
        <TechRow
          testId="tech-row-diameter"
          label="Diametro del fusto"
          namespace="diameter"
          code={diamCat?.category_code}
        />
      )}

      {density && (
        <TechRow
          testId="tech-row-density"
          label="Densità capillare"
          namespace="density"
          code={density.category_code}
        />
      )}

      {mini && (
        <TechRow
          testId="tech-row-miniaturization"
          label="Diversità di diametro"
          namespace="diversity"
          code={mini.category_code}
        />
      )}

      {porphyrin && (
        <TechRow
          testId="tech-row-porphyrin"
          label="Porfirine"
          namespace="porphyrin"
          code={porphyrin.category_code}
        />
      )}

      {sebum && (
        <TechRow
          testId="tech-row-sebum"
          label="Livello di sebo"
          namespace="sebum"
          code={sebum.category_code}
        />
      )}

      {vlm?.dandruff && (
        <TechRow
          testId="tech-row-vlm-dandruff"
          label="Forfora"
          namespace="vlm"
          code={vlm.dandruff.category_code}
        />
      )}

      {vlm?.redness && (
        <TechRow
          testId="tech-row-vlm-redness"
          label="Arrossamento"
          namespace="vlm"
          code={vlm.redness.category_code}
        />
      )}

      {vlm?.occlusion && (
        <TechRow
          testId="tech-row-vlm-occlusion"
          label="Occlusione follicolare"
          namespace="vlm"
          code={vlm.occlusion.category_code}
        />
      )}
    </div>
  );
}

export default function AnalysisDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const vocab = useTricoVocab();
  const [analysis, setAnalysis] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  // ---- Polling pipeline async (mag 2026) -----------------------------------
  // L'analisi può arrivare in 3 stati: "processing" | "completed" | "failed".
  // Se processing → polling leggero ogni 3s su /analyses/{id}/status.
  // Se completed → carichiamo il full report una sola volta.
  // Se failed → schermo di errore dedicato con CTA "Riprova".
  const [pipelineStatus, setPipelineStatus] = useState(null); // {status, progress_stage, error_code, error_message}
  const pollingRef = useRef(null);
  const waitRequested = searchParams.get("wait") === "1";

  // Dialog "Invia analisi" (apr 2026): sostituisce i 3 bottoni separati
  // (Invia WhatsApp / Scarica PDF / Anteprima portale) con un unico flusso
  // centralizzato basato sul link del Portale Cliente.
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sold, setSold] = useState(new Set());
  const [locked, setLocked] = useState(false);

  // Follow-up: foto della baseline (analisi precedente) per lo slider
  // "Prima/Dopo" — caricate solo quando serve, via lo stesso endpoint
  // di dettaglio analisi (nessun endpoint dedicato per le sole foto).
  const [baselinePhotos, setBaselinePhotos] = useState(null);
  const [baselineCreatedAt, setBaselineCreatedAt] = useState(null);

  // Guida consulenza (additiva, dietro flag admin guida_consulenza_enabled):
  // il backend la valorizza solo se il flag è attivo, quindi il bottone
  // resta nascosto finché analysis.guida_consulenza non arriva popolata.
  const [guidaOpen, setGuidaOpen] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  const isFree = user?.plan === "free";
  const canDownloadPdf = !isFree;

  // Carica il full report + client + sales. Usato sia al primo mount (se
  // l'analisi è già completed) sia quando il polling rileva completed.
  const loadFullAnalysis = async () => {
    try {
      const res = await api.get(`/analyses/${id}`);
      setAnalysis(res.data);
      // client e sales sono best-effort: se falliscono il report resta
      // comunque visibile (no reset dello state globale, no toast rumoroso).
      try {
        const c = await api.get(`/clients/${res.data.client_id}`);
        setClient(c.data);
      } catch { /* non critico */ }
      try {
        const s = await api.get(`/analyses/${id}/sales`);
        setSold(new Set(s.data.sold || []));
      } catch { /* non critico */ }
      return res.data;
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && typeof detail === "object" && detail?.error === "analysis_locked") {
        setLocked(true);
      } else if (err.response?.status === 404) {
        toast.error("Analisi non trovata");
        nav("/");
      } else {
        toast.error("Errore nel caricamento dell'analisi");
      }
      return null;
    }
  };

  // Mount: leggiamo prima lo STATUS (leggero, niente foto). Se processing
  // partiamo col polling. Se completed/failed carichiamo il report.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const st = await api.get(`/analyses/${id}/status`);
        if (cancelled) return;
        setPipelineStatus(st.data);
        if (st.data.status === "completed") {
          await loadFullAnalysis();
        } else if (st.data.status === "failed") {
          // Nulla da caricare oltre lo status.
        } else {
          // processing → il polling sotto farà il resto
        }
      } catch (err) {
        if (err.response?.status === 404) {
          toast.error("Analisi non trovata");
          nav("/");
        } else {
          // Errore transiente — mostriamo loader, il polling riproverà.
          setPipelineStatus({ status: "processing", progress_stage: "", error_code: "", error_message: "" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Follow-up: appena il report è caricato, se è un follow-up con una
  // baseline nota carichiamo anche le foto dell'analisi precedente (serve
  // solo per lo slider Prima/Dopo, best-effort — se fallisce la card di
  // confronto foto resta semplicemente nascosta).
  useEffect(() => {
    if (analysis?.tipo_analisi !== "followup") return;
    if (!analysis?.previous_analysis_id) {
      setBaselinePhotos([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/analyses/${analysis.previous_analysis_id}`);
        if (cancelled) return;
        setBaselinePhotos(res.data.photos || []);
        setBaselineCreatedAt(res.data.created_at || null);
      } catch {
        if (!cancelled) setBaselinePhotos([]);
      }
    })();
    return () => { cancelled = true; };
  }, [analysis?.tipo_analisi, analysis?.previous_analysis_id]);

  // Polling ogni 3s finché processing. Max 3 minuti (60 tentativi) per sicurezza.
  useEffect(() => {
    if (!pipelineStatus) return;
    if (pipelineStatus.status !== "processing") return;

    let tries = 0;
    const MAX_TRIES = 60; // 3 minuti @ 3s
    pollingRef.current = setInterval(async () => {
      tries += 1;
      try {
        const st = await api.get(`/analyses/${id}/status`);
        if (st.data.status !== "processing") {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setPipelineStatus(st.data);
          if (st.data.status === "completed") {
            await loadFullAnalysis();
            // Pulisci ?wait=1 dall'URL (UX)
            if (waitRequested) {
              setSearchParams({}, { replace: true });
            }
            toast.success("Analisi pronta");
          }
          // failed → la UI dedicata è già pronta
          return;
        }
        // ancora processing → aggiorniamo solo il progress_stage
        setPipelineStatus(st.data);
      } catch {
        // errore di rete/server — NON blocchiamo, riproveremo al prossimo tick
      }
      if (tries >= MAX_TRIES) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        // Non segnaliamo failed: il backend potrebbe ancora finire. Mostriamo
        // un messaggio gentile e lasciamo all'utente il controllo.
        toast.info("L'analisi sta impiegando più tempo del previsto. Ricarica la pagina tra qualche minuto.");
      }
    }, 3000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineStatus?.status, id]);

  const toggleSold = async (productName) => {
    const isSold = sold.has(productName);
    // Optimistic update
    setSold(prev => {
      const next = new Set(prev);
      if (isSold) next.delete(productName); else next.add(productName);
      return next;
    });
    try {
      if (isSold) {
        await api.delete(`/sales`, { params: { analysis_id: id, product_name: productName } });
      } else {
        await api.post(`/sales`, {
          analysis_id: id,
          client_id: analysis.client_id,
          product_name: productName,
        });
      }
    } catch (err) {
      // Revert on failure
      setSold(prev => {
        const next = new Set(prev);
        if (isSold) next.add(productName); else next.delete(productName);
        return next;
      });
      toast.error("Errore nel salvataggio");
    }
  };

  const downloadPdf = () => {
    if (downloading) return;
    setDownloading(true);
    const ok = downloadAnalysisPdf(id);
    if (ok) {
      toast.success("PDF scaricato");
    } else {
      toast.error("Errore nel download del PDF");
    }
    // Reset downloading state after the browser has had time to initiate the download.
    setTimeout(() => setDownloading(false), 1500);
  };

  // Apre la modale "Invia analisi" e pre-carica il link del portale.
  const openShareDialog = async () => {
    setShareOpen(true);
    setCopied(false);
    if (shareLink) return; // cache per apertura ripetuta
    setShareLoading(true);
    try {
      const res = await api.get(`/analyses/${id}/share-token`);
      const relUrl = res?.data?.url || `/r/${res?.data?.token}`;
      // Costruiamo un link ASSOLUTO (utile per WhatsApp/Email/copia).
      const absUrl = `${window.location.origin}${relUrl}`;
      setShareLink(absUrl);
    } catch {
      toast.error("Impossibile generare il link. Riprova.");
      setShareOpen(false);
    } finally {
      setShareLoading(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success("Link copiato negli appunti");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  const whatsappMessage = shareLink
    ? "Ciao,\n"
      + "Le invio la Sua analisi tricologica personalizzata.\n\n"
      + "Può consultarla comodamente da questo link:\n"
      + `${shareLink}\n\n`
      + "Per qualsiasi chiarimento resto a Sua disposizione."
    : "";
  // wa.me richiede solo le cifre (no "+", no spazi). Il telefono cliente è
  // salvato in formato E.164 (es. "+393331234567") → strippiamo il "+".
  const clientPhoneDigits = (client?.telefono || "").replace(/[^0-9]/g, "");
  const hasClientPhone = clientPhoneDigits.length >= 6;
  const hasClientEmail = !!(client?.email || "").trim();
  const whatsappHref = shareLink && hasClientPhone
    ? `https://wa.me/${clientPhoneDigits}?text=${encodeURIComponent(whatsappMessage)}`
    : "#";
  const emailHref = shareLink && hasClientEmail
    ? `mailto:${encodeURIComponent(client.email)}`
      + `?subject=${encodeURIComponent("La Sua analisi tricologica personalizzata")}`
      + `&body=${encodeURIComponent(
          "Gentile cliente,\n\n"
          + "Le invio la Sua analisi tricologica personalizzata.\n\n"
          + `Può consultarla comodamente da questo link:\n${shareLink}\n\n`
          + "Per qualsiasi chiarimento resto a Sua disposizione.\n\n"
          + "Cordiali saluti."
        )}`
    : "#";

  // Link "brandizzato" da mostrare nell'UI (più corto, più pulito).
  // Il click usa comunque `shareLink` (URL reale con token JWT).
  const prettyShareLink = shareLink
    ? (() => {
        try {
          const u = new URL(shareLink);
          const token = (u.pathname.split("/r/")[1] || "").slice(0, 6);
          return `${u.host}/r/${token}…`;
        } catch {
          return shareLink;
        }
      })()
    : "";

  // Share nativo del device (iOS AirDrop, Android share sheet, ecc.)
  const canNativeShare = typeof navigator !== "undefined"
    && typeof navigator.share === "function";
  const shareNative = async () => {
    if (!canNativeShare || !shareLink) return;
    try {
      await navigator.share({
        title: "Analisi tricologica",
        text: whatsappMessage,
        url: shareLink,
      });
    } catch {
      // utente ha annullato → silenzio
    }
  };

  const clientFileName = (() => {
    const raw = (client?.name || "Cliente").trim() || "Cliente";
    return `Analisi - ${raw.toUpperCase()}.pdf`;
  })();

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="h-12 w-64 shimmer rounded-xl" />
          <div className="h-64 shimmer rounded-2xl" />
        </div>
      </Layout>
    );
  }

  if (locked) {
    return (
      <Layout>
        <Link to="/" className="inline-flex items-center gap-2 text-[#737373] hover:text-[#C5A059] transition-colors mb-8 group">
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          <span className="text-sm">Torna ai clienti</span>
        </Link>
        <div
          className="max-w-2xl mx-auto rounded-2xl border border-[#C5A059] bg-white p-10 text-center animate-fade-up"
          data-testid="analysis-locked-card"
        >
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#C5A059] to-[#B38F4E] flex items-center justify-center mb-6">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">Analisi bloccata</p>
          <h1 className="font-serif-display text-4xl text-[#1A1A1A] mb-4 leading-tight">
            Questa analisi è già stata<br />visualizzata una volta
          </h1>
          <p className="text-[#737373] max-w-lg mx-auto mb-8 leading-relaxed">
            Nel piano <span className="font-semibold text-[#1A1A1A]">Free</span> ogni analisi è
            consultabile una sola volta. Passa a <span className="font-semibold text-[#1A1A1A]">Basic</span>
            {" "}per sbloccare lo storico completo e il download PDF dei tuoi protocolli.
          </p>
          <Link
            to="/piani"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium transition-all"
            data-testid="analysis-locked-upgrade"
          >
            <Crown className="w-4 h-4" />
            Passa a Basic
          </Link>
        </div>
      </Layout>
    );
  }

  // ---- Pipeline async: UI dedicata per processing / failed ----------------
  if (pipelineStatus && pipelineStatus.status === "processing") {
    const stage = pipelineStatus.progress_stage || "queued";
    const stageLabel = {
      queued: "In coda…",
      observation: "Osservazione oggettiva ",
      interpretation: "Interpretazione tricologica (Fase 2/2)",
      protocol: "Generazione protocollo Medavita",
      done: "Finalizzazione…",
    }[stage] || "In elaborazione…";
    return (
      <Layout>
        <Link to="/" className="inline-flex items-center gap-2 text-[#737373] hover:text-[#C5A059] transition-colors mb-8 group" data-testid="back-from-processing">
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          <span className="text-sm">Torna ai clienti</span>
        </Link>
        <div
          className="max-w-2xl mx-auto rounded-2xl border border-[#E5E5E0] bg-white p-10 sm:p-12 text-center animate-fade-up"
          data-testid="analysis-processing-card"
        >
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#FAF6EE] to-[#F3E8CB] border border-[#C5A059]/40 flex items-center justify-center mb-6">
            <Loader2 className="w-7 h-7 text-[#C5A059] animate-spin" />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">Analisi in corso</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1A1A1A] mb-3 leading-tight">
            Stiamo elaborando le immagini
          </h1>
          <p className="text-[#737373] max-w-md mx-auto mb-6 leading-relaxed">
            L'AI sta analizzando la cute e il fusto per generare il protocollo personalizzato.
            L'operazione richiede in genere 15-25 secondi.
          </p>
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FAFAFA] border border-[#E5E5E0] text-sm text-[#1A1A1A]"
            data-testid="analysis-processing-stage"
          >
            <span className="w-2 h-2 rounded-full bg-[#C5A059] animate-pulse" />
            {stageLabel}
          </div>
          <p className="text-xs text-[#A3A3A3] mt-8 leading-relaxed max-w-md mx-auto">
            Puoi chiudere questa pagina in qualsiasi momento: l'analisi continuerà in background e la troverai nella scheda del cliente.
          </p>
        </div>
      </Layout>
    );
  }

  if (pipelineStatus && pipelineStatus.status === "failed") {
    const errMsg = pipelineStatus.error_message
      || "L'analisi non è stata completata. Riprova tra qualche istante.";
    const errCode = pipelineStatus.error_code || "";
    return (
      <Layout>
        <Link to="/" className="inline-flex items-center gap-2 text-[#737373] hover:text-[#C5A059] transition-colors mb-8 group" data-testid="back-from-failed">
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          <span className="text-sm">Torna ai clienti</span>
        </Link>
        <div
          className="max-w-2xl mx-auto rounded-2xl border border-[#F4CDCC] bg-white p-10 sm:p-12 text-center animate-fade-up"
          data-testid="analysis-failed-card"
        >
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#FDF2F2] border border-[#F4CDCC] flex items-center justify-center mb-6">
            <AlertCircle className="w-7 h-7 text-[#D9534F]" />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#D9534F] mb-3">Analisi non completata</p>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1A1A1A] mb-3 leading-tight">
            Qualcosa è andato storto
          </h1>
          <p className="text-[#737373] max-w-md mx-auto mb-8 leading-relaxed">
            {errMsg}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to={`/client/${pipelineStatus.client_id}/analysis`}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium transition-all"
              data-testid="analysis-failed-retry"
            >
              <RefreshCw className="w-4 h-4" />
              Riprova l'analisi
            </Link>
            <Link
              to={`/client/${pipelineStatus.client_id}`}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl border border-[#E5E5E0] hover:border-[#C5A059] text-[#1A1A1A] text-sm font-medium transition-all"
            >
              Torna al cliente
            </Link>
          </div>
          {errCode && (
            <p className="text-[10px] text-[#A3A3A3] mt-6 tracking-wide uppercase">
              Codice errore: {errCode}
            </p>
          )}
        </div>
      </Layout>
    );
  }

  if (!analysis) return null;

  // livello_gravita è ora il category_code di hair_global_health_index
  // (critical/poor/fair/good/excellent) — stile badge per severità, etichetta
  // tradotta via tricoVocab.
  const severityStyles = {
    critical: "bg-red-50 text-red-700 border-red-200",
    poor: "bg-red-50 text-red-700 border-red-200",
    fair: "bg-amber-50 text-amber-700 border-amber-200",
    good: "bg-emerald-50 text-emerald-700 border-emerald-200",
    excellent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };

  return (
    <Layout>
      <Link to={`/client/${analysis.client_id}`} className="inline-flex items-center gap-2 text-[#737373] hover:text-[#C5A059] transition-colors mb-8 group" data-testid="back-to-client-detail">
        <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
        <span className="text-sm">Torna alla scheda cliente</span>
      </Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-10 animate-fade-up">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">Protocollo tricologico</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1A1A1A] mb-3">
            {client?.name}
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-[#737373]">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {new Date(analysis.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
            </span>
            {analysis.livello_gravita && (
              <span className={`text-[10px] tracking-wider uppercase px-3 py-1 rounded-full font-medium border ${severityStyles[analysis.livello_gravita] || ""}`}>
              Stato: {vocab.categoryLabel("global_health", analysis.livello_gravita)}
              </span>
            )}
          </div>
        </div>
        {(() => {
          return (
            <div className="flex flex-wrap items-center gap-3" data-testid="analysis-actions">
              {canDownloadPdf ? (
                <button
                  onClick={openShareDialog}
                  className="h-8 px-5 rounded-xl border border-[#C5A059] text-[#8A6B2E] hover:bg-[#FAF6EE] text-sm font-medium flex items-center gap-2 transition-all"
                  data-testid="open-share-dialog"
                >
                  <Send className="w-4 h-4" />
                  Invia analisi
                </button>
              ) : (
                <Link
                  to="/piani"
                  className="h-12 px-4 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium flex items-center gap-2 transition-all"
                  data-testid="pdf-upgrade-cta"
                >
                  <Crown className="w-4 h-4" />
                  Sblocca invio con Basic
                </Link>
              )}
            </div>
          );
        })()}
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-12 gap-6">
        {/* Scalp & Hair Profile — indici sintetici TricoProtocol — e, affiancata,
            Profilo tecnico (parametri grezzi in formato tabellare). Le
            fotografie sono spostate in una riga propria sotto (vedi oltre). */}
        <ScalpHealthProfile analysis={analysis} />
        <TechnicalProfileCard analysis={analysis} />

        {/* Photos — riga propria a larghezza piena */}
        {analysis.photos?.length > 0 && (
          <div className="col-span-12 rounded-2xl border border-[#E5E5E0] bg-white p-6 animate-fade-up">
            <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium mb-4">Fotografie analizzate</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {analysis.photos.map((p, i) => (
                <div key={i} className="rounded-xl overflow-hidden bg-[#FAFAFA]">
                  <button
                    type="button"
                    onClick={() => setLightboxPhoto(p)}
                    className="block w-full aspect-square overflow-hidden cursor-zoom-in"
                    data-testid={`photo-thumb-${i}`}
                  >
                    <img src={`data:image/jpeg;base64,${p.data}`} alt={p.label} className="w-full h-full object-cover" />
                  </button>
                  <p className="text-xs text-center py-2 text-[#737373]">{p.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Follow-up comparison block — shown in the lead slot when
            tipo_analisi == "followup". Aggiuntivo: non sostituisce mai
            la diagnosi corrente, solo la contestualizza rispetto alla baseline. */}
        {analysis.tipo_analisi === "followup" && (
          <div
            className="col-span-12 rounded-2xl border border-[#C5A059]/40 bg-[#FAF6EE]/70 p-8 animate-fade-up"
            data-testid="followup-section"
          >
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-white border border-[#C5A059]/40 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-[#8A6B2E]" />
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-[#8A6B2E] font-medium">Follow-up</p>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-serif-display text-2xl text-[#1A1A1A]">
                    Evoluzione rispetto alla baseline
                  </p>
                  {analysis.followup_anticipato && (
                    <span
                      className="inline-flex items-center text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full font-medium bg-amber-50 text-amber-700 border border-amber-200"
                      data-testid="followup-anticipato-badge"
                      title="Questo follow-up è stato eseguito prima dei 30 giorni consigliati"
                    >
                      Anticipato
                    </span>
                  )}
                </div>
              </div>
              {analysis.previous_analysis_id && (
                <Link
                  to={`/analysis/${analysis.previous_analysis_id}`}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] hover:border-[#C5A059] text-[#1A1A1A] transition-colors"
                  data-testid="followup-baseline-link"
                >
                  <GitCompare className="w-3.5 h-3.5" />
                  Apri analisi precedente
                </Link>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-5 items-start">
              <FollowupIndicesChart analysis={analysis} />
              <PhotoCompareCard
                analysis={analysis}
                baselinePhotos={baselinePhotos}
                baselineCreatedAt={baselineCreatedAt}
              />
            </div>
          </div>
        )}

        {/* Objective observation */}
        {analysis.osservazione_oggettiva && (
          <div className="col-span-12 rounded-2xl border border-[#E5E5E0] bg-white p-8 animate-fade-up">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#FAFAFA] border border-[#E5E5E0] flex items-center justify-center">
                  <Eye className="w-5 h-5 text-[#737373]" />
                </div>
                <div>
                  <p className="font-serif-display text-2xl text-[#1A1A1A]">Osservazione oggettiva</p>
                </div>
              </div>
              {Object.keys(analysis.guida_consulenza || {}).length > 0 && (
                <button
                  type="button"
                  onClick={() => setGuidaOpen(true)}
                  className="h-9 px-4 rounded-xl border border-[#C5A059] text-[#8A6B2E] hover:bg-[#FAF6EE] text-sm font-medium flex items-center gap-2 transition-all shrink-0"
                  data-testid="open-guida-consulenza"
                >
                  <MessageCircle className="w-4 h-4" />
                  Guida consulenza
                </button>
              )}
            </div>
            <p className="text-xs text-[#737373] italic mb-4">
              Descrizione testuale dei parametri ed indici rilevati.
            </p>
            <p className="text-[#1A1A1A] leading-relaxed text-[15px]" data-testid="observation-text">
              {analysis.osservazione_oggettiva}
            </p>
          </div>
        )}

        {/* Guida consulenza — modale con i 4 blocchi da usare dal vivo col
            cliente (cosa sta succedendo / come spiegarlo / cosa proporre /
            errori da evitare). Popolata dal backend solo se il flag admin
            guida_consulenza_enabled è attivo, vedi guida_consulenza.build_guida_consulenza. */}
        {/* Lightbox — apre a schermo intero la foto cliccata tra quelle
            analizzate, per ispezione di dettaglio durante la consulenza. */}
        <Dialog open={!!lightboxPhoto} onOpenChange={(open) => !open && setLightboxPhoto(null)}>
          <DialogContent className="max-w-3xl p-2 bg-transparent border-none shadow-none" data-testid="photo-lightbox">
            <DialogTitle className="sr-only">{lightboxPhoto?.label || "Foto"}</DialogTitle>
            {lightboxPhoto && (
              <>
                <img
                  src={`data:image/jpeg;base64,${lightboxPhoto.data}`}
                  alt={lightboxPhoto.label}
                  className="w-full max-h-[80vh] object-contain rounded-xl"
                />
                <p className="text-center text-sm text-white/90 pt-2">{lightboxPhoto.label}</p>
              </>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={guidaOpen} onOpenChange={setGuidaOpen}>
          <DialogContent className="max-w-lg" data-testid="guida-consulenza-dialog">
            <DialogHeader>
              <DialogTitle>Guida consulenza</DialogTitle>
              <DialogDescription>
                Spunti pratici da usare dal vivo con il cliente.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-5 text-sm text-[#1A1A1A]">
              {analysis.guida_consulenza?.cosa_sta_succedendo?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium mb-2">
                    Cosa sta succedendo
                  </p>
                  <ul className="space-y-1.5 list-disc list-inside">
                    {analysis.guida_consulenza.cosa_sta_succedendo.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {analysis.guida_consulenza?.come_spiegarlo && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium mb-2">
                    Come spiegarlo
                  </p>
                  <p className="italic text-[#737373]">"{analysis.guida_consulenza.come_spiegarlo}"</p>
                </div>
              )}
              {analysis.guida_consulenza?.cosa_proporre && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium mb-2">
                    Cosa proporre
                  </p>
                  <p>{analysis.guida_consulenza.cosa_proporre}</p>
                </div>
              )}
              {analysis.guida_consulenza?.errori_da_evitare?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium mb-2">
                    Errori da evitare
                  </p>
                  <ul className="space-y-1.5 list-disc list-inside text-[#737373]">
                    {analysis.guida_consulenza.errori_da_evitare.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Nota Professionale (additiva — collassabile, parte chiusa) */}
        <div className="col-span-12 animate-fade-up">
          <ProfessionalNote />
        </div>

        {/* Goals */}
        {analysis.obiettivi?.length > 0 && (
          <div className="col-span-12 lg:col-span-5 rounded-2xl border border-[#E5E5E0] bg-white p-6 animate-fade-up">
            <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium mb-4">Principali obiettivi del trattamento</p>
            <ul className="space-y-3">
              {analysis.obiettivi.map((g, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#C5A059] shrink-0 mt-0.5" />
                  <span className="text-[#1A1A1A] text-sm leading-relaxed">{vocab.needObiettivo(g.code)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Home routine — real Medavita products with priority + usage steps */}
        {/* INTERVENTO 3 / passo 3c — Dichiarazioni della cliente
            (questionario pre-analisi). Renderizzato solo se almeno una
            risposta è presente. Inserito subito prima della routine
            domiciliare. */}
        <QuestionnaireDeclarations questionnaire={analysis.cute_questionnaire} />

        {analysis.routine_domiciliare?.length > 0 && (
          <div className="col-span-12 rounded-2xl border border-[#E5E5E0] bg-white p-8 animate-fade-up">
            <div className="flex items-center gap-2 mb-5">
              <Home className="w-4 h-4 text-[#C5A059]" />
              <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium">Routine domiciliare</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {analysis.routine_domiciliare.map((r, i) => {
                const pr = r.priority_level || "";
                const sched = r.scheduling_label || "";
                const badgeLabel = pr === "primary" ? "Primario"
                  : pr === "secondary" ? "Secondario"
                  : pr === "supportive" ? "Opzionale"
                  : "";
                const badgeCls = pr === "primary" ? "bg-[#1A1A1A] text-white"
                  : pr === "secondary" ? "bg-[#C5A059] text-white"
                  : "bg-white text-[#737373] border border-[#E5E5E0]";
                const schedCls = r.scheduling_mode === "alternanza"
                  ? "bg-[#FFF5E5] text-[#C87D2A] border border-[#F2D9A7]"
                  : r.scheduling_mode === "fase_successiva"
                  ? "bg-[#FAFAFA] text-[#737373] border border-dashed border-[#C5A059]"
                  : r.scheduling_mode === "fase_attiva"
                  ? "bg-[#E8F2EC] text-[#1E6B47] border border-[#C3DECE]"
                  : "";
                return (
                  <div
                    key={i}
                    className="p-5 rounded-xl border border-[#E5E5E0] hover:border-[#C5A059] bg-[#FAFAFA] transition-colors"
                    data-testid={`home-routine-item-${i}`}
                  >
                    <div className="flex items-start gap-4 mb-2">
                      <ProductThumb src={r.img_prodotto} alt={r.nome_prodotto} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              {badgeLabel && (
                                <span className={`inline-block text-[9px] tracking-wider uppercase px-2 py-0.5 rounded-full font-semibold ${badgeCls}`}>
                                  {badgeLabel}
                                </span>
                              )}
                              {sched && (
                                <span
                                  className={`inline-block text-[9px] tracking-wider uppercase px-2 py-0.5 rounded-full font-semibold ${schedCls}`}
                                  data-testid={`scheduling-${r.scheduling_mode}`}
                                >
                                  {sched}
                                </span>
                              )}
                            </div>
                            <p className="font-serif-display text-lg text-[#1A1A1A] leading-tight">{r.nome_prodotto}</p>
                            {r.linea && (
                              <p className="text-[10px] uppercase tracking-wider text-[#737373] mt-1">
                                Linea {r.linea}
                              </p>
                            )}
                          </div>
                          {r.categoria && (
                            <span className="shrink-0 text-[10px] tracking-wider uppercase px-2.5 py-1 rounded-full bg-white border border-[#E5E5E0] text-[#737373] font-medium">
                              {vocab.categoriaProdotto(r.categoria)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {r.funzione && (
                      <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium mb-2 mt-3">
                        Funzione: {r.funzione.replaceAll('_', ' ')}
                      </p>
                    )}
                    {r.scheduling_note && (
                      <p className="text-xs text-[#737373] italic mb-2 border-l-2 border-[#C5A059] pl-3 leading-relaxed">
                        {r.scheduling_note}
                      </p>
                    )}
                    <div className="border-t border-[#E5E5E0] pt-3 mt-3 space-y-1">
                      {r.frequenza && (
                        <p className="text-xs text-[#737373]">
                          <span className="font-medium text-[#1A1A1A]">Frequenza:</span> {r.frequenza}
                        </p>
                      )}
                      {r.formato && (
                        <p className="text-xs text-[#737373]">
                          <span className="font-medium text-[#1A1A1A]">Confezione:</span> {r.formato}
                        </p>
                      )}
                      {r.codice_prodotto && (
                        <p className="text-xs text-[#737373]">
                          <span className="font-medium text-[#1A1A1A]">Cod.</span> {r.codice_prodotto}
                        </p>
                      )}
                      {r.prezzo_eur != null && (
                        <p className="text-xs" data-testid={`home-price-${i}`}>
                          <span className="font-medium text-[#1A1A1A]">Prezzo:</span>{" "}
                          <span className="text-[#C5A059] font-semibold">{formatEuro(r.prezzo_eur)}</span>
                        </p>
                      )}
                      <label
                        className="mt-3 inline-flex items-center gap-2 cursor-pointer select-none"
                        data-testid={`sold-home-${i}`}
                      >
                        <input
                          type="checkbox"
                          checked={sold.has(r.nome_prodotto)}
                          onChange={() => toggleSold(r.nome_prodotto)}
                          className="w-4 h-4 accent-[#C5A059] cursor-pointer"
                        />
                        <span className={`text-xs font-medium ${sold.has(r.nome_prodotto) ? "text-[#C5A059]" : "text-[#737373]"}`}>
                          {sold.has(r.nome_prodotto) ? "Venduto ✓" : "Venduto"}
                        </span>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Usage instructions */}
            <div className="mt-8 pt-6 border-t border-[#E5E5E0]">
              <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium mb-4">
                Modalità di utilizzo a casa
              </p>
              <div className="space-y-4">
                {analysis.routine_domiciliare.map((r, i) => (
                  <div key={i} className="p-4 rounded-xl bg-[#FAFAFA] border border-[#E5E5E0]" data-testid={`usage-steps-${i}`}>
                    <p className="text-sm font-medium text-[#1A1A1A] mb-3">
                      {r.nome_prodotto}
                      {r.categoria && <span className="text-xs text-[#737373] ml-2">({vocab.categoriaProdotto(r.categoria)})</span>}
                    </p>
                    <ol className="space-y-3 ml-1">
                      {(r.modalita_utilizzo || []).map((s, j) => {
                        const step = typeof s === "string" ? s : s?.step;
                        const beneficio = typeof s === "string" ? "" : s?.beneficio;
                        return (
                          <li key={j} className="text-sm flex gap-2.5">
                            <span className="text-[#C5A059] font-semibold shrink-0">{j + 1}.</span>
                            <div className="flex-1">
                              <p className="text-[#1A1A1A]">{step}</p>
                              {beneficio && (
                                <p className="text-xs text-[#737373] italic mt-0.5 flex gap-1.5">
                                  <span className="text-[#C5A059]">→</span>
                                  <span>{beneficio}</span>
                                </p>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Salon protocol — real operational procedure */}
        {analysis.protocollo_salone && (analysis.protocollo_salone.prodotti?.length > 0 || analysis.protocollo_salone.procedura_operativa?.length > 0) && (
          <div className="col-span-12 rounded-2xl border border-[#1A1A1A] bg-[#1A1A1A] text-white p-8 animate-fade-up">
            <div className="flex items-center gap-2 mb-6">
              <Scissors className="w-4 h-4 text-[#C5A059]" />
              <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium">Protocollo Salone</p>
              <span className="ml-auto text-sm text-white/70">
                {analysis.protocollo_salone.frequenza || "1 volta al mese"}
              </span>
            </div>

            {/* Products used */}
            {analysis.protocollo_salone.prodotti?.length > 0 && (
              <div className="mb-8">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059]/80 mb-3 font-medium">
                  Prodotti utilizzati
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  {analysis.protocollo_salone.prodotti.map((p, i) => {
                    const pr = p.priority_level || "";
                    const badgeLabel = pr === "primary" ? "Primario"
                      : pr === "secondary" ? "Secondario"
                      : pr === "supportive" ? "Opzionale"
                      : "";
                    const badgeCls = pr === "primary" ? "bg-white text-[#1A1A1A]"
                      : pr === "secondary" ? "bg-[#C5A059] text-white"
                      : "bg-white/10 text-white/80 border border-white/20";
                    return (
                      <div key={i} className="p-5 rounded-xl bg-white/5 border border-white/10 hover:border-[#C5A059]/50 transition-colors" data-testid={`salon-product-${i}`}>
                        {badgeLabel && (
                          <span className={`inline-block text-[9px] tracking-wider uppercase px-2 py-0.5 rounded-full font-semibold mb-2 ${badgeCls}`}>
                            {badgeLabel}
                          </span>
                        )}
                        <p className="font-serif-display text-lg text-white leading-tight">{p.nome_prodotto}</p>
                        <p className="text-[10px] uppercase tracking-wider text-white/50 mt-1">
                          Linea {p.linea} · {vocab.categoriaProdotto(p.categoria)}
                        </p>
                        {p.funzione && (
                          <p className="text-xs text-[#C5A059] italic mt-3">{p.funzione.replaceAll('_', ' ')}</p>
                        )}
                        {(p.codice_prodotto || p.formato) && (
                          <p className="text-xs text-white/50 mt-2">
                            {p.codice_prodotto && <span>Cod. {p.codice_prodotto}</span>}
                            {p.codice_prodotto && p.formato && " • "}
                            {p.formato && <span>{p.formato}</span>}
                          </p>
                        )}
                        <p className="text-[10px] text-white/40 italic mt-3 uppercase tracking-wider">
                          Trattamento professionale — non in vendita
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Operational procedure */}
            {analysis.protocollo_salone.procedura_operativa?.length > 0 && (
              <div className="mb-8">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059]/80 mb-3 font-medium">
                  Procedura operativa
                </p>
                <ol className="space-y-2">
                  {analysis.protocollo_salone.procedura_operativa.map((step, i) => (
                    <li key={i} className="text-sm text-white/90 leading-relaxed pl-4 border-l-2 border-[#C5A059]/40" data-testid={`procedure-step-${i}`}>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Objective */}
            {analysis.protocollo_salone.obiettivo_trattamento && (
              <div className="pt-6 border-t border-white/10">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-2 font-medium">
                  Obiettivo del trattamento in salone
                </p>
                <p className="text-sm text-white/90 leading-relaxed" data-testid="salon-objective">
                  {analysis.protocollo_salone.obiettivo_trattamento}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Riepilogo prodotti consigliati — shopping list with total (home routine ONLY) */}
        {analysis.riepilogo_prodotti?.prodotti?.length > 0 && (
          <div className="col-span-12 rounded-2xl border border-[#C5A059] bg-white p-8 animate-fade-up" data-testid="riepilogo-section">
            <div className="flex items-center gap-2 mb-2">
              <Receipt className="w-4 h-4 text-[#C5A059]" />
              <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium">
                Riepilogo Prodotti Consigliati
              </p>
            </div>
            <p className="text-xs text-[#737373] italic mb-5">
              Prodotti da acquistare per la routine domiciliare
            </p>

            <div className="divide-y divide-[#E5E5E0]">
              {analysis.riepilogo_prodotti.prodotti.map((p, i) => (
                <div
                  key={i}
                  className="grid grid-cols-12 gap-3 items-start py-3"
                  data-testid={`riepilogo-item-${i}`}
                >
                  <div className="col-span-12 md:col-span-8">
                    <p className="text-sm font-medium text-[#1A1A1A] leading-snug">
                      {p.nome_prodotto}
                    </p>
                  </div>
                  <div className="col-span-6 md:col-span-2 text-sm text-[#737373]">
                    {p.formato || "—"}
                  </div>
                  <div
                    className="col-span-6 md:col-span-2 text-sm text-[#1A1A1A] font-semibold md:text-right"
                    data-testid={`riepilogo-price-${i}`}
                  >
                    {formatEuro(p.prezzo_eur)}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 pt-5 border-t-2 border-[#1A1A1A] flex items-baseline justify-between">
              <p className="text-sm uppercase tracking-[0.2em] text-[#1A1A1A] font-semibold">
                Totale prodotti
              </p>
              <p
                className="text-2xl font-serif-display text-[#C5A059] font-semibold"
                data-testid="riepilogo-total"
              >
                {formatEuro(analysis.riepilogo_prodotti.totale_eur || 0)}
              </p>
            </div>

            <p className="mt-3 text-[11px] text-[#737373] italic leading-relaxed">
              I prodotti del protocollo salone sono professionali e non sono
              inclusi in questo riepilogo.
            </p>
          </div>
        )}

      </div>

      {/* Dialog "Condividi analisi" — layout in stile Bridal Manager */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-[640px] p-0 overflow-hidden" data-testid="share-dialog">
          <div className="px-7 sm:px-8 pt-7 pb-5">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-[28px] sm:text-3xl text-[#1A1A1A] leading-tight">
                Condividi analisi
              </DialogTitle>
              <DialogDescription className="text-[15px] text-[#737373] leading-relaxed mt-2">
                Il cliente riceverà un link interattivo con il protocollo personalizzato.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="px-7 sm:px-8 pb-8 space-y-5">
            {shareLoading || !shareLink ? (
              <div className="h-28 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-[#C5A059] animate-spin" />
              </div>
            ) : (
              <>
                {/* Box file analisi (stile Bridal) */}
                <div
                  data-testid="share-file-box"
                  className="flex items-center gap-4 rounded-xl border border-[#E8E0CE] bg-[#FDFBF4] px-5 py-4"
                >
                  <div className="w-10 h-10 rounded-lg border border-[#E8E0CE] bg-white flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-medium text-[#1A1A1A] truncate">
                      {clientFileName}
                    </p>
                    <p className="text-xs text-[#737373] mt-0.5">Pronto</p>
                  </div>
                </div>

                {/* Griglia azioni 2x2 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <ShareTile
                    testid="share-copy-btn"
                    icon={copied ? Check : LinkIcon}
                    iconClass={copied ? "text-[#16A34A]" : "text-[#C5A059]"}
                    title={copied ? "Link copiato" : "Copia link cliente"}
                    subtitle={copied ? "Appunti aggiornati" : prettyShareLink}
                    onClick={copyShareLink}
                  />
                  <ShareTile
                    testid="share-email-btn"
                    icon={Mail}
                    iconClass="text-[#C5A059]"
                    title={hasClientEmail ? "Invia via Email" : "Email cliente mancante"}
                    subtitle={hasClientEmail ? client.email : "Aggiungi l'email al profilo cliente"}
                    href={hasClientEmail ? emailHref : undefined}
                    onClick={hasClientEmail ? undefined : () => toast.info("Aggiungi un indirizzo email nel profilo cliente per usare questa azione.")}
                    disabled={!hasClientEmail}
                  />
                  <ShareTile
                    testid="share-whatsapp-btn"
                    icon={MessageCircle}
                    iconClass={hasClientPhone ? "text-[#25D366]" : "text-[#A3A3A3]"}
                    title={hasClientPhone ? "Invia via WhatsApp" : "Telefono cliente mancante"}
                    subtitle={
                      hasClientPhone
                        ? `Invia a ${client.telefono}`
                        : "Aggiungi il telefono al profilo cliente"
                    }
                    href={hasClientPhone ? whatsappHref : undefined}
                    external={hasClientPhone}
                    onClick={hasClientPhone ? undefined : () => toast.info("Aggiungi un numero di telefono nel profilo cliente per usare questa azione.")}
                    disabled={!hasClientPhone}
                  />
                  <ShareTile
                    testid="share-download-pdf-btn"
                    icon={Download}
                    iconClass="text-[#C5A059]"
                    title="Scarica PDF"
                    subtitle="Salva il documento sul dispositivo"
                    onClick={downloadPdf}
                    disabled={downloading}
                  />
                  {canNativeShare && (
                    <ShareTile
                      testid="share-native-btn"
                      icon={Share2}
                      iconClass="text-[#C5A059]"
                      title="Condividi…"
                      subtitle="Menu nativo (AirDrop, Mail, …)"
                      onClick={shareNative}
                    />
                  )}
                </div>

                <p className="text-[12px] text-[#A3A3A3] leading-relaxed pt-2 border-t border-[#F2EEE7]">
                  Il link apre una pagina interattiva con il protocollo completo
                  e i contatti del Suo salone. Scade dopo 24 ore.
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function ShareTile({ icon: Icon, iconClass = "text-[#C5A059]", title, subtitle, onClick, href, external, disabled, testid }) {
  const Component = href ? "a" : "button";
  const extraProps = href
    ? { href, ...(external ? { target: "_blank", rel: "noopener noreferrer" } : {}) }
    : { type: "button", onClick, disabled };
  return (
    <Component
      {...extraProps}
      data-testid={testid}
      className="group flex items-start gap-4 rounded-xl border border-[#E5E5E0] bg-white hover:border-[#C5A059] hover:bg-[#FDFBF4] transition-all px-4 py-4 text-left disabled:opacity-60 disabled:cursor-not-allowed"
    >
      <div className="w-10 h-10 rounded-lg bg-[#FAFAFA] border border-[#E5E5E0] flex items-center justify-center shrink-0 group-hover:bg-white">
        <Icon className={`w-5 h-5 ${iconClass}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[15px] font-medium text-[#1A1A1A] leading-snug truncate">
          {title}
        </p>
        <p className="text-xs text-[#737373] mt-0.5 truncate">{subtitle}</p>
      </div>
    </Component>
  );
}

// Nota Professionale — additiva. Collassata di default ad ogni apertura
// della pagina. Nessun localStorage (richiesta esplicita).
function ProfessionalNote() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="rounded-2xl border border-[#C5A059]/30 bg-[#FBF8F0] transition-all duration-200"
      data-testid="professional-note"
    >
      {!expanded ? (
        <div className="flex items-center gap-3 px-5 py-3">
          <Info className="w-3.5 h-3.5 text-[#8A6B2E] shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A6B2E] font-semibold leading-tight">
              Nota Professionale
            </p>
            <p className="text-[12px] text-[#5B4A24] leading-snug truncate sm:whitespace-normal mt-0.5">
              AI TricoProtocol™ è uno strumento di supporto cosmetico-tricologico professionale.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center gap-1 text-[11px] text-[#8A6B2E] hover:text-[#5B4A24] font-medium shrink-0 transition-colors"
            data-testid="professional-note-toggle"
          >
            Approfondisci <ChevronDown className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div className="px-5 py-4" data-testid="professional-note-expanded">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Info className="w-3.5 h-3.5 text-[#8A6B2E]" />
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A6B2E] font-semibold">
                Nota Professionale
              </p>
            </div>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="inline-flex items-center gap-1 text-[11px] text-[#8A6B2E] hover:text-[#5B4A24] font-medium transition-colors"
              data-testid="professional-note-toggle"
            >
              Comprimi <ChevronUp className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-2 text-[12.5px] text-[#5B4A24] leading-relaxed">
            <p>
              AI TricoProtocol™ è uno strumento di supporto all&apos;analisi
              cosmetico-tricologica professionale.
            </p>
            <p>
              Le informazioni riportate hanno finalità cosmetiche e
              professionali e non sostituiscono una valutazione specialistica
              quando necessaria.
            </p>
            <p>
              In presenza di anomalie o condizioni che richiedano ulteriori
              approfondimenti, si raccomanda di indirizzare il cliente a uno
              specialista qualificato.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

